import React, { useState, useEffect } from 'react';
import styles from './ReloadPrompt.module.css';

interface ReloadPromptProps {
  isVisible: boolean;
  onReload: () => void;
  onDismiss: () => void;
  autoReloadDelay?: number; // in milliseconds
}

function ReloadPrompt({
  isVisible,
  onReload,
  onDismiss,
  autoReloadDelay = 10000 // 10 seconds default
}: ReloadPromptProps) {
  const [countdown, setCountdown] = useState(autoReloadDelay / 1000);
  const [isAutoReloading, setIsAutoReloading] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setCountdown(autoReloadDelay / 1000);
      setIsAutoReloading(false);
      return;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsAutoReloading(true);
          onReload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [isVisible, autoReloadDelay, onReload]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.prompt}>
        <div className={styles.icon}>🔄</div>
        <h3 className={styles.title}>New Version Available</h3>
        <p className={styles.message}>
          A new version of the app is available. Please reload to get the latest changes and
          improvements.
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.button} ${styles.primary}`}
            onClick={onReload}
            disabled={isAutoReloading}
          >
            {isAutoReloading ? 'Reloading...' : 'Reload Now'}
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.secondary}`}
            onClick={onDismiss}
            disabled={isAutoReloading}
          >
            Dismiss
          </button>
        </div>
        {countdown > 0 && !isAutoReloading && (
          <p className={styles.countdown}>
            Auto-reloading in {countdown} second{countdown !== 1 ? 's' : ''}...
          </p>
        )}
      </div>
    </div>
  );
}

export default ReloadPrompt;
